import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import API from "../api/api";

export default function TournamentRulesPublic() {
  const { id } = useParams();

  const [tournament, setTournament] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadRules() {
      try {
        setLoading(true);
        setError("");

        const data = await API.get(`/tournaments/${id}/rules`);

        setTournament(data.tournament);
        setRules(Array.isArray(data.rules) ? data.rules : []);
      } catch (err) {
        console.error("Failed to load tournament rules:", err);
        setError(err.message || "Failed to load fight rules");
      } finally {
        setLoading(false);
      }
    }

    if (id) {
      loadRules();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">📋</div>
          <p className="text-gray-400">Loading fight rules...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-950/40 border border-red-800 rounded-2xl p-8 text-center">
            <div className="text-4xl mb-4">⚠️</div>

            <h1 className="text-xl font-bold mb-2">
              Unable to Load Fight Rules
            </h1>

            <p className="text-red-300 mb-6">
              {error}
            </p>

            <Link
              to="/tournaments"
              className="inline-block bg-gray-800 hover:bg-gray-700 px-5 py-3 rounded-xl"
            >
              Back to Tournaments
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">

      {/* ================================================= */}
      {/* HERO */}
      {/* ================================================= */}

      <section className="relative overflow-hidden border-b border-gray-800">

        <div className="absolute inset-0 bg-gradient-to-b from-[#d4af37]/10 via-transparent to-black" />

        <div className="relative max-w-5xl mx-auto px-6 py-12 md:py-16">

          <Link
            to={`/tournaments/${id}/register`}
            className="inline-flex items-center gap-2 text-gray-400 hover:text-[#d4af37] mb-8 transition"
          >
            ← Back to Tournament
          </Link>

          <div className="flex items-center gap-3 mb-4">

            <span className="px-3 py-1 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#d4af37] text-xs font-bold uppercase tracking-widest">
              Official Rules
            </span>

          </div>

          <h1 className="text-3xl md:text-5xl font-black tracking-tight">
            FIGHT RULES
          </h1>

          {tournament && (
            <p className="mt-3 text-xl md:text-2xl text-gray-300 font-semibold">
              {tournament.name}
            </p>
          )}

          <p className="mt-4 text-gray-500 max-w-2xl">
            Please read the official rules carefully before
            registering for this tournament.
          </p>

        </div>
      </section>

      {/* ================================================= */}
      {/* CONTENT */}
      {/* ================================================= */}

      <main className="max-w-5xl mx-auto px-6 py-10 md:py-14">

        {rules.length === 0 ? (

          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-10 text-center">

            <div className="text-5xl mb-5">
              📋
            </div>

            <h2 className="text-xl font-bold mb-2">
              Rules Have Not Been Published Yet
            </h2>

            <p className="text-gray-500">
              The official fight rules for this tournament
              will be published by the organizer.
            </p>

          </div>

        ) : (

          <div className="space-y-5">

            {rules.map((rule, index) => (

              <section
                key={rule.id}
                className="bg-gray-900/80 border border-gray-800 rounded-2xl overflow-hidden shadow-lg"
              >

                {/* SECTION HEADER */}

                <div className="flex items-center gap-4 px-5 md:px-7 py-5 border-b border-gray-800">

                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#d4af37] text-black flex items-center justify-center font-black">
                    {index + 1}
                  </div>

                  <h2 className="text-lg md:text-xl font-black uppercase tracking-wide text-[#d4af37]">
                    {rule.section_title}
                  </h2>

                </div>

                {/* SECTION CONTENT */}

                <div className="px-5 md:px-7 py-6">

                  <div className="text-gray-300 whitespace-pre-wrap leading-8 text-[15px] md:text-base">
                    {rule.content}
                  </div>

                </div>

              </section>

            ))}

          </div>

        )}

        {/* ================================================= */}
        {/* BOTTOM CTA */}
        {/* ================================================= */}

        {rules.length > 0 && (
          <div className="mt-10 bg-gradient-to-r from-[#d4af37]/10 to-transparent border border-[#d4af37]/30 rounded-2xl p-6 md:p-8">

            <h3 className="text-xl font-black mb-2">
              Ready to compete?
            </h3>

            <p className="text-gray-400 mb-5">
              Make sure you understand the rules before
              completing your tournament registration.
            </p>

            <Link
              to={`/tournaments/${id}/register`}
              className="inline-flex items-center justify-center bg-[#d4af37] text-black font-black px-7 py-3 rounded-xl hover:bg-[#e5c158] transition"
            >
              🥊 REGISTER FOR THIS TOURNAMENT
            </Link>

          </div>
        )}

      </main>

    </div>
  );
}
